import React, { useRef } from "react";
import { ArrowDown, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskReveal, smoothScrollTo } from "../lib/motion";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            ref={ref}
            id="home"
            data-testid="hero-section"
            className="relative w-full min-h-screen overflow-hidden flex items-center bg-ocean-deep grain"
        >
            {/* Parallax abstract graphic from deck */}
            <motion.div
                className="absolute inset-0 pointer-events-none"
                aria-hidden="true"
                style={{ y: bgY, scale: bgScale }}
            >
                <div
                    className="absolute inset-0 bg-no-repeat bg-cover bg-center opacity-90"
                    style={{
                        backgroundImage:
                            "url('https://customer-assets.emergentagent.com/job_startup-blur-design/artifacts/ansfkbll_image.png')",
                    }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(90deg, rgba(8,27,46,0.92) 0%, rgba(8,27,46,0.75) 35%, rgba(8,27,46,0.25) 70%, rgba(8,27,46,0) 100%)",
                    }}
                />
            </motion.div>

            {/* Content */}
            <motion.div
                className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-32 w-full"
                style={{ y: contentY, opacity: contentOpacity }}
            >
                <div className="max-w-4xl">
                    <motion.h1
                        data-testid="hero-heading"
                        className="font-display font-black text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter"
                        initial={{ opacity: 0, filter: "blur(12px)", y: 20 }}
                        animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
                        transition={{ duration: 1.1, ease: EASE, delay: 0.1 }}
                    >
                        Ocean<span className="text-cyan">To</span>Oasis
                    </motion.h1>

                    <p className="mt-6 font-display font-light text-white/90 text-2xl md:text-4xl tracking-tight max-w-3xl">
                        <MaskReveal
                            delay={0.35}
                            stagger={0.1}
                            lines={[
                                <>
                                    Climate{" "}
                                    <span className="text-energy font-black">
                                        Positive
                                    </span>{" "}
                                    Desalination.
                                </>,
                            ]}
                        />
                    </p>

                    <motion.p
                        className="mt-6 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed"
                        data-testid="hero-subtext"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.6 }}
                    >
                        Desalination doesn't have to cost energy. We turn the
                        waste heat and spent chemistry of coastal industry into
                        fresh water.
                    </motion.p>

                    <motion.div
                        className="mt-10 flex flex-wrap gap-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
                    >
                        <button
                            onClick={() => smoothScrollTo("products")}
                            data-testid="hero-cta-technology"
                            className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-energy text-ocean font-bold text-sm tracking-wide hover:bg-white transition-all duration-300 shadow-[0_10px_40px_rgba(163,230,53,0.3)] hover:-translate-y-0.5"
                        >
                            Explore the technology
                            <ArrowDown
                                size={16}
                                className="transition-transform duration-300 group-hover:translate-y-0.5"
                            />
                        </button>
                        <Link
                            to="/consulting"
                            data-testid="hero-cta-consulting"
                            className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-energy text-ocean font-bold text-sm tracking-wide hover:bg-white transition-all duration-300 shadow-[0_10px_40px_rgba(163,230,53,0.3)] hover:-translate-y-0.5"
                        >
                            <Briefcase size={16} />
                            Desalination Consulting
                        </Link>
                        <a
                            href="mailto:info@oceantooasis.in"
                            data-testid="hero-cta-contact"
                            className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-energy text-ocean font-bold text-sm tracking-wide hover:bg-white transition-all duration-300 shadow-[0_10px_40px_rgba(163,230,53,0.3)] hover:-translate-y-0.5"
                        >
                            Talk to the team
                        </a>
                    </motion.div>
                </div>
            </motion.div>

            {/* Scroll indicator */}
            <button
                onClick={() => smoothScrollTo("opening-statement")}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/60 hover:text-white transition"
                aria-label="Scroll down"
                data-testid="hero-scroll-indicator"
            >
                <span className="text-[10px] uppercase tracking-[0.3em] font-semibold">
                    Scroll
                </span>
                <ArrowDown size={18} className="animate-pulse-soft" />
            </button>
        </section>
    );
};

export default Hero;
